import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { shapes, Filter } from '@unite-us/client-utils';
import { Spinner } from 'common/spinners';
import { DynamicTable } from 'common/tables';
import { Drawer } from '@unite-us/ui';
import firstLine from 'common/utils/firstLine';
import CaseRowIcon from 'src/components/Dashboard/components/Cases/CaseRowIcon';
import { TOP_NAVIGATION_HEIGHT } from 'src/components/Navigation/constants';
import _ from 'lodash';
import {
  showFacesheetCasesTableDescription,
  hideMoreFiltersOption,
  crtb2646ShowProgramNameReferralsCasesDashboard,
} from 'src/common/utils/FeatureFlags/flags';
import {
  clientNameColumn,
  serviceTypeColumn,
  programColumn,
} from '../../utils/columnConfigs/baseColumns';
import FilterBar from '../FilterBar';
import { setFilterSearchPlaceholder, setAsyncSearch } from '../../utils/filter';
import './ClosedCasesIndex.scss';

export function ClosedCasesIndex(props) {
  const {
    paging,
    data,
    onNextClick,
    onPrevClick,
    isFetching,
    onCellClick,
    initialSort,
    isCC,
    filters,
    onFiltersChange,
    isCaseMgmtProvider,
    fetchGroupsUsers,
    onClose,
    onToggle,
    open,
    showDescription,
    showMoreFilters,
    showProgramNameColumn,
    sortConfig,
    setSort,
  } = props;
  const secondaryFilters = filters.filter((f) => _.get(f, 'secondary'));

  const onSortColumn = (column, sortDirection) => {
    if (sortConfig) {
      sortConfig(column, sortDirection);
    }
  };

  const getColumns = () => {
    const descColumn = {
      label: 'Description',
      formatData: (item) => firstLine(item.description),
      className: 'truncate',
    };

    return _.compact([
      clientNameColumn(setSort, onSortColumn),
      showDescription ? descColumn : null,
      serviceTypeColumn(),
      ...(showProgramNameColumn ? [programColumn('program.name', setSort, onSortColumn)] : []),
      {
        label: 'Date Opened',
        sortColumn: 'opened_date',
        sortBy: setSort?.sidx,
        sortDirection: setSort?.sord,
        setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
        type: 'date',
      },
      {
        label: 'Last Updated',
        sortColumn: 'updated_at',
        sortBy: setSort?.sidx,
        sortDirection: setSort?.sord,
        setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
        type: 'date',
      },
    ]);
  };

  return (
    <div>
      <h1 className="sr-only" id="dashboard-main-content-heading">Internal Cases - Closed</h1>
      <FilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        paging={paging}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
        isFetching={isFetching && !_.isEmpty(data)}
        asyncSearch={fetchGroupsUsers}
        onMoreFiltersClick={onToggle}
        showSecondaryFilters={!showMoreFilters}
      />
      <div className="dashboard-inner-content">
        {
          (isFetching && _.isEmpty(data)) ?
            <Spinner /> : (
              <DynamicTable
                caption="Closed cases"
                id="closed-cases-table"
                dataSet={data}
                onCellClick={onCellClick}
                initialSort={initialSort}
                columnLabels={getColumns()}
                rowIcon={(serviceCase) => (
                  <CaseRowIcon
                    isCC={isCC}
                    isCaseMgmtProvider={isCaseMgmtProvider}
                    serviceCase={serviceCase}
                  />
                )}
                emptyMessage="There are no closed cases."
              />
            )
        }
      </div>
      <Drawer
        className="closed-cases__drawer"
        open={open}
        secondary
        onClose={onClose}
        topOffset={TOP_NAVIGATION_HEIGHT}
      >
        <div className="closed-cases__drawer-contents">
          <div className="closed-cases__drawer-header">
            <h2 className="closed-cases__drawer-header-label">
              More Filter(s)
            </h2>
          </div>
          <div className="closed-cases__drawer-body">
            {
                _.map(_.orderBy(secondaryFilters, ['name'], ['desc']), (f) => {
                  const {
                    asyncSearch: filterAsyncSearch,
                    searchPlaceholder,
                  } = f;

                  if (!f.secondary) {
                    return null;
                  }

                  return (
                    <Filter
                      hideSelectAll={f.hideSelectAll || false}
                      key={f.key}
                      name={f.name}
                      pluralName={f.pluralName}
                      options={f.options}
                      onFiltersChange={(activeFilters) => onFiltersChange(f.key, activeFilters)}
                      searchPlaceholder={searchPlaceholder || setFilterSearchPlaceholder(f)}
                      asyncSearch={filterAsyncSearch !== undefined ?
                        setAsyncSearch(f, filterAsyncSearch) :
                        setAsyncSearch(f, fetchGroupsUsers)}
                      uniqIdPrefix={f.key}
                    />
                  );
                })
              }
          </div>
        </div>
      </Drawer>
    </div>
  );
}

ClosedCasesIndex.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  paging: shapes.paging,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  onCellClick: PropTypes.func.isRequired,
  initialSort: PropTypes.object,
  isCC: PropTypes.bool.isRequired,
  filters: PropTypes.array,
  onFiltersChange: PropTypes.func,
  isCaseMgmtProvider: PropTypes.bool,
  fetchGroupsUsers: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  showDescription: PropTypes.bool,
  showMoreFilters: PropTypes.bool,
  showProgramNameColumn: PropTypes.bool,
  sortConfig: PropTypes.func,
  setSort: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    showDescription: showFacesheetCasesTableDescription(state),
    showMoreFilters: hideMoreFiltersOption(state),
    showProgramNameColumn: crtb2646ShowProgramNameReferralsCasesDashboard(state),
  };
}

export default connect(mapStateToProps)(ClosedCasesIndex);
